import React, { useState, useEffect } from 'react';
import { Modal, Box, Button } from '@mui/material';
import { X, AlertCircle } from 'lucide-react';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  TabsContainer,
  TabsList,
  TabButton,
  SectionHeading,
  ContentHeader,
  TabIndicator,
  NoRisksContainer,
  NoRisksText,
} from './styles';
import { RiskLevel } from 'types/enums';
import { IRiskMatrixViewModel, IRiskViewModel } from 'viewModels';
import { useExternalFont } from 'contexts/externalFontContext';
import FontList from 'components/FontList';

interface RiskDetailsModalProps {
  open: boolean;
  onClose: () => void;
  currentMatrix: IRiskMatrixViewModel;
}

const getRiskLevelColor = (level: RiskLevel) => {
  switch (level) {
    case RiskLevel.HIGH:
      return '#f44336';
    case RiskLevel.MEDIUM:
      return '#ff9800';
    case RiskLevel.LOW:
      return '#4caf50';
    default:
      return '#757575';
  }
};

const RiskDetailsModal = ({
  open,
  onClose,
  currentMatrix,
}: RiskDetailsModalProps) => {
  const { externalFonts } = useExternalFont();
  const [currentRisk, setCurrentRisk] = useState<
    IRiskViewModel | undefined
  >();
  const [activeRiskTab, setActiveRiskTab] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (open && currentMatrix?.risks?.length > 0) {
      setCurrentRisk(currentMatrix.risks[0]);
      setActiveRiskTab(currentMatrix.risks[0].name);
    } else {
      setCurrentRisk(undefined);
      setActiveRiskTab(undefined);
    }
  }, [open, currentMatrix]);

  const handleTabClick = (riskName: string) => {
    setActiveRiskTab(riskName);
    const selectedRisk = currentMatrix.risks.find(
      (risk) => risk.name === riskName
    );
    setCurrentRisk(selectedRisk);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalWrapper>
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="modal-title">
              Riscos da Matriz: {currentMatrix?.name || 'Sem Nome'}
            </ModalTitle>
            <ModalDescription id="modal-description">
              Visualize as fontes selecionadas para cada risco
            </ModalDescription>
          </ModalTitleContainer>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <ContentHeader>
            <SectionHeading>Riscos da matriz</SectionHeading>

            <Box
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <TabsContainer>
                <TabsList role="tablist">
                  {currentMatrix?.risks?.map((risk) => (
                    <TabButton
                      key={risk.name}
                      active={activeRiskTab === risk.name}
                      onClick={() => handleTabClick(risk.name)}
                      role="tab"
                      aria-selected={activeRiskTab === risk.name}
                      aria-controls={`panel-${risk.name}`}
                      tabIndex={0}
                    >
                      <TabIndicator
                        sx={{
                          backgroundColor: getRiskLevelColor(
                            risk.level
                          ),
                        }}
                      />
                      <span>{risk.name}</span>
                      {risk.level === RiskLevel.HIGH
                        ? ' (ALTO)'
                        : risk.level === RiskLevel.MEDIUM
                        ? ' (MÉDIO)'
                        : ' (BAIXO)'}
                    </TabButton>
                  ))}
                </TabsList>
              </TabsContainer>
            </Box>
          </ContentHeader>
          {currentRisk ? (
            <FontList
              fonts={externalFonts}
              selectedFonts={currentRisk.externalFontsKeys}
              showSelectedOnly={true}
              showMetrics={false}
            />
          ) : (
            <NoRisksContainer>
              <AlertCircle size={24} color="#D81B60" />
              <NoRisksText>
                Esta matriz não possui riscos.
              </NoRisksText>
            </NoRisksContainer>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              bgcolor: '#293778',
              '&:hover': { bgcolor: '#1e2654' },
            }}
          >
            Fechar
          </Button>
        </ModalFooter>
      </ModalWrapper>
    </Modal>
  );
};

export default RiskDetailsModal;
